import type {
  AircraftCatalog,
  ArrivalInquiry,
  ZoneGeometry,
  ZoneSlot,
  ZoneSlotGrid,
} from './types'

/** Variable pitch sizes slots to this footprint percentile (denser than the max). */
const VARIABLE_PITCH_PERCENTILE = 0.75
/** Fallback nest ratio (tail width ÷ wingspan) when an area authors none. */
const DEFAULT_TAIL_WIDTH_RATIO = 0.6
/** Sane slot extent when the fleet has no resolvable footprints. */
const FALLBACK_EXTENT_FT = 50

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round(p * (sorted.length - 1))))
  return sorted[idx]
}

/** Footprint values along an axis for the offered fleet. */
function axisValues(
  fleet: ArrivalInquiry[],
  catalog: AircraftCatalog,
  axis: 'wingspan' | 'length'
): number[] {
  const vals: number[] = []
  for (const inq of fleet) {
    const fp = catalog.get(inq.typeCode)?.footprint
    if (fp) vals.push(fp[axis])
  }
  return vals
}

/**
 * The along-lane slot extent (feet) on the PROGRESSION axis (wingspan for
 * side_by_side, length for nose_to_tail). Uniform pitch uses the authored size
 * cap if set, otherwise the largest footprint so every aircraft fits a slot.
 * Variable pitch sizes to the 75th-percentile footprint — denser slots, with
 * the oversized minority rejected per-slot in `scheduleReuse`. Computed from the
 * FULL fleet (never a prefix) so the grid stays stable as the placer advances.
 */
export function computeUniformExtent(
  zone: ZoneGeometry,
  fleet: ArrivalInquiry[],
  catalog: AircraftCatalog
): number {
  const axis = zone.progression === 'side_by_side' ? 'wingspan' : 'length'
  const cap = zone.progression === 'side_by_side' ? zone.maxWingspanFt : zone.maxLengthFt
  if (zone.pitchMode === 'uniform' && cap && cap > 0) return cap

  const vals = axisValues(fleet, catalog, axis)
  if (vals.length === 0) return FALLBACK_EXTENT_FT
  if (zone.pitchMode === 'variable') {
    return percentile([...vals].sort((a, b) => a - b), VARIABLE_PITCH_PERCENTILE)
  }
  return Math.max(...vals)
}

/**
 * The cross-lane (depth) extent (feet) on the OTHER axis (length for
 * side_by_side, wingspan for nose_to_tail). Only the 2D patterns
 * (checkerboard/free_stack) pack rows along depth, so this gates how many rows
 * fit. Uniform = largest footprint; variable = 75th percentile.
 */
export function computeCrossExtent(
  zone: ZoneGeometry,
  fleet: ArrivalInquiry[],
  catalog: AircraftCatalog
): number {
  const axis = zone.progression === 'side_by_side' ? 'length' : 'wingspan'
  const vals = axisValues(fleet, catalog, axis)
  if (vals.length === 0) return FALLBACK_EXTENT_FT
  if (zone.pitchMode === 'variable') {
    return percentile([...vals].sort((a, b) => a - b), VARIABLE_PITCH_PERCENTILE)
  }
  return Math.max(...vals)
}

/** Floor on the nested stride factor so compressed rows never overlap hulls. */
const MIN_NEST_FACTOR = 0.5

/** A row of slots stacked along the zone DEPTH: its center, facing, and width offset. */
interface RowPlan {
  centerLocalZ: number
  headingDeg: number
  /** extra inset along WIDTH (interleaved rows stagger by half a slot, extent/2) */
  widthOffset: number
}

/**
 * Lay out the rows (along the zone DEPTH) for a pattern + coupling. `pattern`
 * sets the row COUNT (`row`=1, `double_row`=lanes, `checkerboard`/`free_stack`=
 * fill the depth, but never fewer than `lanes`); `coupling` + `pattern` set how
 * adjacent rows RELATE, decomposed into four per-row attributes:
 *
 *  • `opposing` (coupling `tail_to_tail` | `nested`) — alternate rows face
 *    base+180°, so the two rows point opposite ways across a shared spine.
 *  • `noseToSpine` (pattern `checkerboard` = the Nose-to-Nose formation) — flips
 *    the OPPOSITE row parity vs the tails-to-spine layouts, so the noses point at
 *    the center spine and meet there (mirror of tail-to-tail). Deterministic
 *    regardless of the base heading, which is what makes Nose-to-Nose visibly
 *    distinct from Tail-to-Tail.
 *  • `interleaved` (pattern `checkerboard` OR coupling `nested`) — odd rows shift
 *    half a slot in X, so noses/tails tuck into the gaps between the other row's
 *    aircraft ("nose between nose") instead of lining up head-on.
 *  • `nestFactor` (coupling `nested`) — compresses the DEPTH stride by the tail-
 *    width ratio so each row's narrow tail section overlaps into the opposing
 *    row's wing zone (the zipper), floored at MIN_NEST_FACTOR so hulls can't fully
 *    overlap.
 *
 * Combined: `checkerboard + tail_to_tail` = Nose-to-Nose (noses meet, interleaved);
 * `double_row + tail_to_tail` = Tail-to-Tail (tails meet, aligned); `free_stack +
 * nested` = Nested (tails meet, interleaved, depth-compressed zipper).
 *
 * Row DEPTH spacing is simplified by default (`lanes` rows spread evenly across
 * the depth); `zone.footprintRows` opts into footprint-driven tight packing. The
 * 2D patterns fill the depth, but always render at least `lanes` rows so the
 * opposing 2-row formations stay visible on shallow areas (we compress to an even
 * spread rather than collapse to a single row).
 */
function planRows(zone: ZoneGeometry, alongExtentFt: number, crossExtentFt: number): RowPlan[] {
  const margin = zone.safetyMarginFt
  const baseHeading = zone.placementHeadingDeg
  const lanes = Math.max(1, Math.min(6, zone.lanes))

  const opposing = zone.coupling === 'tail_to_tail' || zone.coupling === 'nested'
  // Nose-to-Nose (checkerboard): noses point at the shared center spine — mirror
  // the flip parity of the tails-to-spine layouts so the two are always distinct.
  const noseToSpine = zone.pattern === 'checkerboard'
  const interleaved = zone.pattern === 'checkerboard' || zone.coupling === 'nested'
  const nestFactor =
    zone.coupling === 'nested'
      ? Math.max(MIN_NEST_FACTOR, zone.tailWidthRatio ?? DEFAULT_TAIL_WIDTH_RATIO)
      : 1

  const headingFor = (r: number): number => {
    if (!opposing) return baseHeading
    const flip = noseToSpine ? r % 2 === 0 : r % 2 === 1
    return flip ? (baseHeading + 180) % 360 : baseHeading
  }
  const offsetFor = (r: number): number =>
    interleaved && r % 2 === 1 ? alongExtentFt / 2 : 0

  /** Exactly `count` rows spread evenly across the full depth. */
  const evenRows = (count: number): RowPlan[] => {
    const rows: RowPlan[] = []
    const rowDepth = zone.depth / count
    for (let r = 0; r < count; r++) {
      rows.push({
        centerLocalZ: (r + 0.5) * rowDepth - zone.depth / 2,
        headingDeg: headingFor(r),
        widthOffset: offsetFor(r),
      })
    }
    return rows
  }

  /** Rows packed by their cross-axis footprint from the front edge, up to `cap`. */
  const packedRows = (cap: number): RowPlan[] => {
    const stride = crossExtentFt * nestFactor + margin
    const rows: RowPlan[] = []
    let top = margin
    let r = 0
    while (top + crossExtentFt <= zone.depth && r < cap) {
      rows.push({
        centerLocalZ: top + crossExtentFt / 2 - zone.depth / 2,
        headingDeg: headingFor(r),
        widthOffset: offsetFor(r),
      })
      top += stride
      r++
    }
    return rows
  }

  const stacked = zone.pattern === 'checkerboard' || zone.pattern === 'free_stack'

  if (!stacked) {
    // row / double_row: exactly `lanes` rows. Footprint-tight from the front edge
    // when it fits; otherwise spread evenly so we never drop below `lanes`.
    if (zone.footprintRows) {
      const packed = packedRows(lanes)
      if (packed.length >= lanes) return packed
    }
    return evenRows(lanes)
  }

  // checkerboard / free_stack: fill the depth, but never fewer than `lanes` rows —
  // on a shallow area fall back to an even spread of `lanes` rows so Nose-to-Nose
  // and Nested still render their opposing/interleaved geometry.
  const packed = packedRows(Infinity)
  return packed.length >= lanes ? packed : evenRows(lanes)
}

/**
 * The along-lane slot-size sequence (feet) the wing-to-wing (variable-pitch) laydown
 * cycles through — sized to actual hull widths on the progression axis, weighted by
 * fleet COMPOSITION and evenly distributed (NOT the set of distinct sizes).
 *
 * Why composition, not distinct sizes: cycling the unique set gives every distinct
 * hull EQUAL representation, so a fleet of (say) 30 light singles + 2 jets lays down
 * slots alternating small/large. That inflates the effective pitch and scatters the
 * light aircraft across the zone with large gaps — they read as shrunken / lost.
 * Sizing slots proportional to how many of each hull actually arrive lets the
 * dominant small types pack wingtip-to-wingtip, while the rarer large hulls are
 * spread evenly through the cycle (greedy "next-due" distribution) so they still
 * surface within any row width — even a short prefix stays representative — and
 * don't spill to overflow.
 */
export function alongFootprints(
  zone: ZoneGeometry,
  fleet: ArrivalInquiry[],
  catalog: AircraftCatalog
): number[] {
  const axis = zone.progression === 'side_by_side' ? 'wingspan' : 'length'
  const counts = new Map<number, number>()
  for (const inq of fleet) {
    const fp = catalog.get(inq.typeCode)?.footprint
    if (fp) counts.set(fp[axis], (counts.get(fp[axis]) ?? 0) + 1)
  }
  if (counts.size === 0) return []

  // Greedy even-distribution: each distinct size is "due" every total/count steps; at
  // each slot emit the size whose next-due mark is earliest. Spreads the rare large
  // hulls through the cycle instead of blocking them at one end. Seeded ascending so
  // ties resolve smallest-first (deterministic), with a half-stride phase so the marks
  // don't all collide at 0.
  const total = [...counts.values()].reduce((a, b) => a + b, 0)
  const cursors = [...counts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([size, count]) => ({ size, stride: total / count, next: total / count / 2, left: count }))

  const out: number[] = []
  for (let i = 0; i < total; i++) {
    let pick: (typeof cursors)[number] | null = null
    for (const c of cursors) {
      if (c.left <= 0) continue
      if (!pick || c.next < pick.next) pick = c
    }
    if (!pick) break
    out.push(pick.size)
    pick.left--
    pick.next += pick.stride
  }
  return out
}

/**
 * Build a zone's fixed slot grid. Lanes/rows run along the zone DEPTH (local Z);
 * slots within a row pack along the WIDTH (local X). Each slot is a stable
 * position a departing aircraft frees for a later arrival to reuse.
 *
 * `alongExtentFt` is the uniform slot footprint on the progression axis;
 * `crossExtentFt` (defaults to it) gates how many rows the 2D patterns stack along
 * depth. When `variableExtents` is supplied (Wing-to-wing / variable pitch) the
 * row is packed with per-aircraft-width slots cycled from that sorted list
 * instead of one uniform box, so each hull packs wingtip-to-wingtip + margin.
 *
 * Reuses the EXACT cursor + Three.js Y-rotation convention from
 * `lib/utils/unit-position.ts` so slots align with the rendered zone rect. Do NOT
 * reinvent the rotation — the rot-90 self-check guards it.
 */
export function buildSlotGrid(
  zone: ZoneGeometry,
  alongExtentFt: number,
  crossExtentFt: number = alongExtentFt,
  variableExtents?: number[]
): ZoneSlot[] {
  const slots: ZoneSlot[] = []
  if (alongExtentFt <= 0) return slots

  const margin = zone.safetyMarginFt
  const rotationRad = (zone.rotationDeg * Math.PI) / 180
  const cos = Math.cos(rotationRad)
  const sin = Math.sin(rotationRad)

  const rows = planRows(zone, alongExtentFt, crossExtentFt)

  // Wing-to-wing: size each slot to an actual hull, cycling a composition-weighted
  // sequence of fleet footprints (light types dominate; large hulls spread evenly) so
  // the common small aircraft pack tight instead of being spread apart by big slots.
  // Uniform pitch keeps one fixed slot box for the whole zone.
  const useVariable =
    zone.pitchMode === 'variable' && !!variableExtents && variableExtents.length > 0

  rows.forEach((row, laneIndex) => {
    let leftEdge = margin + row.widthOffset // start a margin in (plus checkerboard stagger)
    let slotIndex = 0
    let cycle = 0
    while (true) {
      let extent = useVariable ? variableExtents![cycle % variableExtents!.length] : alongExtentFt
      if (leftEdge + extent > zone.width) {
        if (!useVariable) break
        // The cycled size won't fit the remaining width — pack the row's tail with the
        // smallest hull that still fits. The cycle is distribution-ordered (not
        // ascending), so scan for the minimum fit rather than taking the first.
        let fit = Infinity
        for (const e of variableExtents!) {
          if (leftEdge + e <= zone.width && e < fit) fit = e
        }
        if (!isFinite(fit)) break
        extent = fit
      }
      const centerLocalX = leftEdge + extent / 2 - zone.width / 2
      const centerLocalZ = row.centerLocalZ

      // Three.js Y-rotation (NOT standard 2D rotation) — matches unit-position.ts.
      const rotatedX = centerLocalX * cos + centerLocalZ * sin
      const rotatedZ = -centerLocalX * sin + centerLocalZ * cos

      slots.push({
        areaId: zone.areaId,
        laneIndex,
        slotIndex,
        position: [zone.center[0] + rotatedX, zone.center[1] + rotatedZ],
        rotationDeg: row.headingDeg,
        extentFt: extent,
      })

      leftEdge += extent + margin
      slotIndex++
      cycle++
    }
  })

  return slots
}

/** Build the slot grid for every zone, sorted by fillPriority ascending. */
export function buildZoneSlotGrids(
  zones: ZoneGeometry[],
  fleet: ArrivalInquiry[],
  catalog: AircraftCatalog
): ZoneSlotGrid[] {
  return [...zones]
    .sort((a, b) => a.fillPriority - b.fillPriority)
    .map((zone) => {
      const uniformExtentFt = computeUniformExtent(zone, fleet, catalog)
      const crossExtentFt = computeCrossExtent(zone, fleet, catalog)
      const variableExtents =
        zone.pitchMode === 'variable' ? alongFootprints(zone, fleet, catalog) : undefined
      return {
        areaId: zone.areaId,
        name: zone.name,
        fillPriority: zone.fillPriority,
        uniformExtentFt,
        progression: zone.progression,
        maxWingspanFt: zone.maxWingspanFt,
        maxLengthFt: zone.maxLengthFt,
        slots: buildSlotGrid(zone, uniformExtentFt, crossExtentFt, variableExtents),
      }
    })
}
